import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { pazientiAPI } from '../../services/api';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';
import SelezioneRicerca from '../SelezioneRicerca';
import { PROVINCE_ITALIANE } from '../../constants/categorie';
import { validaDocumento, nascitaDalCF, pulisciCodice } from '../../utils/codiceFiscale';
import { erroreEmailCambiata, avvisoEmail } from '../../utils/email';
import CampoDataNascita from '../CampoDataNascita';
import AvvisoCap from '../AvvisoCap';
import RicercaComune from '../RicercaComune';
import RicercaStato from '../RicercaStato';
import CampoDocumento from '../CampoDocumento';
import { useAuthStore } from '../../store/useStore';
import { messaggioDa } from '../../utils/notifiche';
import { residenzaInItalia, senzaResidenzaNonArrivata } from '../../utils/residenza';
import '../../styles/PazienteForm.css';

const PazienteForm = ({ paziente, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        nome: '', cognome: '', codice_fiscale: '', tipo_documento: 'cf_italiano', data_nascita: '',
        luogo_nascita: '', provincia_nascita: '', stato_nascita: '', sesso: 'M', telefono: '', email: '',
        indirizzo: '', citta: '', cap: '', provincia: '', stato_residenza: '', note: '',
        longevity: false
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [erroreCF, setErroreCF] = useState('');
    /* Un codice che non corrisponde a data o sesso e' un AVVISO, non un
       blocco (Alex, 26/09/2026): omocodie ed errori dell'Agenzia esistono. */
    const [avvisoCF, setAvvisoCF] = useState('');
    const [giaInArchivio, setGiaInArchivio] = useState(null);
    /* Dopo il salvataggio, se l'intestatario delle fatture ha ancora il
       codice di prima: l'avviso resta a video (non una notifica che sparisce)
       e l'amministrazione lo allinea con un clic (Alex, 2/11/2026). La
       segreteria vede l'avviso senza il pulsante: il server le risponde 403. */
    const [dopo, setDopo] = useState(null);
    const ruolo = useAuthStore((s) => s.user?.ruolo);
    const navigate = useNavigate();
    /* Chiudere con qualcosa di diverso da com'era chiede conferma: clic
       sullo sfondo, X e «Annulla» passano tutti da chiudi(). */
    const [versione, setVersione] = useState(0);
    const sporco = useSporco(formData, versione);
    const chiedi = useUscitaProtetta(sporco);
    const chiudi = () => { if (chiedi()) onClose(); };

    useEffect(() => {
        if (paziente) {
            setFormData({
                nome: paziente.nome || '',
                cognome: paziente.cognome || '',
                codice_fiscale: paziente.codice_fiscale || '',
                /* IL TIPO DI DOCUMENTO SI CARICA (3/10/2026). Senza, ogni
                   paziente partiva «codice fiscale italiano»: il codice ISS di
                   un sammarinese era «da correggere» e «Modifica dati» non
                   salvava (verifica del 23/09, punto 18). Con loro arrivano
                   provincia e Stato di nascita, che il modulo non mostrava. */
                tipo_documento: paziente.tipo_documento || 'cf_italiano',
                data_nascita: paziente.data_nascita ? paziente.data_nascita.split('T')[0] : '',
                luogo_nascita: paziente.luogo_nascita || '',
                provincia_nascita: paziente.provincia_nascita || '',
                stato_nascita: paziente.stato_nascita || '',
                sesso: paziente.sesso || 'M',
                telefono: paziente.telefono || '',
                email: paziente.email || '',
                indirizzo: paziente.indirizzo || '',
                citta: paziente.citta || '',
                cap: paziente.cap || '',
                provincia: paziente.provincia || '',
                /* LO STATO DI RESIDENZA SI CARICA E SI SALVA (4/10/2026).
                   Fino a quel giorno questo modulo non l'aveva: «Modifica
                   dati» non lo poteva correggere (solo rifacendo la
                   pratica), e l'avviso sul CAP guardava una casella che non
                   c'era, cioe' trattava da italiano chi abita all'estero. */
                stato_residenza: paziente.stato_residenza || '',
                note: paziente.note || '',
                longevity: paziente.longevity === true
            });
        }
        setVersione((v) => v + 1);
    }, [paziente]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setFormData(prev => ({ ...prev, [name]: checked }));
            return;
        }
        const v = name === 'codice_fiscale' ? pulisciCodice(value) : value;
        setFormData(prev => ({ ...prev, [name]: v }));
        if (name === 'codice_fiscale') setErroreCF('');
    };

    /* Con 26.000 anagrafiche importate, molti di quelli che si
       presentano come nuovi sono gia' in archivio: si cerca appena
       il codice e' completo, cosi' l'operatore se ne accorge prima
       di riscrivere tutto. */
    const cercaInArchivio = async (cf) => {
        if (!cf || cf.length !== 16) { setGiaInArchivio(null); return; }
        if (paziente?.codice_fiscale === cf) { setGiaInArchivio(null); return; }
        try {
            const r = await api.get('/pazienti/verifica-cf', { params: { cf } });
            setGiaInArchivio(r.data.data?.trovato ? r.data.data : null);
        } catch (e) {
            setGiaInArchivio(null);
        }
    };

    // Verifica del CF al termine dell'inserimento, con i dati anagrafici correnti
    const verificaCF = () => {
        if (!formData.codice_fiscale) { setErroreCF(''); return; }
        const tipo = formData.tipo_documento || 'cf_italiano';
        const esito = validaDocumento(formData.codice_fiscale, tipo, {
            data_nascita: formData.data_nascita,
            sesso: formData.sesso
        });
        setErroreCF(esito.valido ? '' : esito.errore);
        setAvvisoCF(esito.valido ? (esito.avvisi[0] || '') : '');
        /* Data e sesso dal codice, se sono vuoti: il campo lo dice. Solo da
           un codice fiscale: da un ISS, un passaporto o un STP non si ricava niente. */
        const n = esito.valido && tipo === 'cf_italiano' ? nascitaDalCF(formData.codice_fiscale) : null;
        if (n && (!formData.data_nascita || !formData.sesso)) {
            setFormData((p) => ({ ...p, data_nascita: p.data_nascita || n.data_nascita, sesso: p.sesso || n.sesso }));
            setAvvisoCF('Data di nascita letta dal codice fiscale: controlla che sia giusta.');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Blocco sul codice fiscale prima di inviare, secondo il tipo di documento
        const esito = validaDocumento(formData.codice_fiscale, formData.tipo_documento || 'cf_italiano', {
            data_nascita: formData.data_nascita,
            sesso: formData.sesso
        });
        if (!esito.valido) {
            setErroreCF(esito.errore);
            setError('Correggi il codice fiscale prima di salvare');
            return;
        }

        /* La forma dell'email, con la regola del server (utils/email.js):
           quella nuova o cambiata. Il campo «email» del browser ne ha una
           sua, piu' larga (accetta «nome@dominio» senza il punto), e da
           sola faceva partire una richiesta che il server rifiuta. Un'email
           gia' in archivio senza forma non ferma chi corregge il telefono:
           lo stesso fanno il server e il database. */
        const rifiutoEmail = erroreEmailCambiata(formData.email, paziente?.email);
        if (rifiutoEmail) {
            setError(rifiutoEmail);
            return;
        }

        setLoading(true);
        try {
            if (paziente) {
                /* Se la scheda da cui si e' aperto il modulo non portava lo
                   Stato di residenza, la casella vuota non si manda: per il
                   server vuoto e' «Italia» (utils/residenza.js). */
                const r = await pazientiAPI.update(paziente.id, senzaResidenzaNonArrivata(formData, paziente));
                /* Il codice e' cambiato e l'intestatario delle fatture ha
                   ancora quello di prima: lo dice il server (2/11/2026), il
                   modulo lo mostra e basta. */
                const avviso = r?.data?.data?.avviso_intestatario;
                if (avviso) {
                    onSuccess();
                    setDopo({ avviso, intestatari: r.data.data.intestatari_da_allineare || [], esito: null, errore: null, inCorso: false });
                    return;
                }
            } else {
                await pazientiAPI.create(formData);
            }
            onSuccess();
            onClose();
        } catch (err) {
            setError(err.message || 'Errore durante il salvataggio');
        } finally {
            setLoading(false);
        }
    };

    const allinea = async () => {
        setDopo((d) => ({ ...d, inCorso: true, errore: null }));
        try {
            const messaggi = [];
            for (const id of dopo.intestatari) {
                const r = await api.post(`/fatturazione/soggetti/${id}/allinea-alla-scheda`);
                messaggi.push(r.data.message);
            }
            setDopo((d) => ({ ...d, inCorso: false, esito: messaggi.join(' ') }));
        } catch (e) {
            setDopo((d) => ({ ...d, inCorso: false, errore: messaggioDa(e, 'Errore nell\'allineamento dell\'intestatario') }));
        }
    };

    if (dopo) {
        return (
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()} role="dialog" data-avviso-intestatario>
                    <div className="modal-header">
                        <h2>Paziente aggiornato</h2>
                        <button className="btn-close" onClick={onClose}>✕</button>
                    </div>
                    {dopo.esito
                        ? <div className="pf-esito-intestatario">{dopo.esito}</div>
                        : <div className="pf-avviso-intestatario" role="alert">{dopo.avviso}</div>}
                    {dopo.errore && <div className="error-message">{dopo.errore}</div>}
                    <div className="modal-footer">
                        <button type="button" onClick={onClose} className="btn-secondary" disabled={dopo.inCorso}>
                            Chiudi
                        </button>
                        {ruolo === 'admin' && !dopo.esito && dopo.intestatari.length > 0 && (
                            <button type="button" onClick={allinea} className="btn-primary" disabled={dopo.inCorso}>
                                {dopo.inCorso ? 'Allineamento...' : 'Allinea l\'intestatario alla scheda'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="modal-overlay" onClick={chiudi}>
            <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{paziente ? 'Modifica paziente' : 'Nuovo paziente'}</h2>
                    <button className="btn-close" onClick={chiudi}>✕</button>
                </div>
                <form onSubmit={handleSubmit}>
                    {error && <div className="error-message">{error}</div>}
                    <div className="form-section">
                        <h3>Dati anagrafici</h3>
                        <div className="form-grid">
                            <div className="form-group">
                                <label>Nome *</label>
                                <input type="text" name="nome" value={formData.nome} onChange={handleChange} required />
                            </div>
                            <div className="form-group">
                                <label>Cognome *</label>
                                <input type="text" name="cognome" value={formData.cognome} onChange={handleChange} required />
                            </div>
                            <div className="form-group">
                                <label>Codice fiscale *</label>
                                <CampoDocumento
                                    valore={formData.codice_fiscale}
                                    tipo={formData.tipo_documento || 'cf_italiano'}
                                    onChange={(v) => {
                                        setFormData((p) => ({ ...p, codice_fiscale: v }));
                                        cercaInArchivio(v);
                                    }}
                                    onTipoChange={(v) => setFormData((p) => ({ ...p, tipo_documento: v }))}
                                    anagrafica={{
                                        data_nascita: formData.data_nascita,
                                        sesso: formData.sesso,
                                    }} />
                                {erroreCF && <span className="campo-errore">{erroreCF}</span>}
                                {!erroreCF && avvisoCF && <span className="campo-avviso">{avvisoCF}</span>}

                                {giaInArchivio && (
                                    <div className="pf-gia-archivio">
                                        <div>
                                            <b>Questo paziente è già in archivio</b>
                                            <p>
                                                {giaInArchivio.cognome} {giaInArchivio.nome}
                                                {giaInArchivio.id_paziente ? ` · ${giaInArchivio.id_paziente}` : ''}
                                                {giaInArchivio.nel_cestino
                                                    ? ' — si trova nel cestino, va recuperato da lì'
                                                    : giaInArchivio.da_completare
                                                        ? ' — anagrafica da completare'
                                                        : ''}
                                            </p>
                                        </div>
                                        {!giaInArchivio.nel_cestino && (
                                            <button
                                                type="button"
                                                onClick={() => navigate(`/pazienti/${giaInArchivio.id}`)}
                                            >
                                                Apri la scheda
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="form-group">
                                <label>Sesso *</label>
                                <select name="sesso" value={formData.sesso} onChange={handleChange} onBlur={verificaCF}>
                                    <option value="M">Maschio</option>
                                    <option value="F">Femmina</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Data di nascita *</label>
                                <CampoDataNascita valore={formData.data_nascita}
                                    onChange={(v) => setFormData((p) => ({ ...p, data_nascita: v }))}
                                    onBlur={verificaCF} />
                            </div>
                            <div className="form-group">
                                <label>Luogo di nascita</label>
                                <RicercaComune
                                    valore={formData.luogo_nascita}
                                    codiceFiscale={formData.codice_fiscale}
                                    statoEstero={formData.stato_nascita}
                                    onChange={(v) => setFormData((p) => ({ ...p, luogo_nascita: v }))}
                                    onScelta={(c) => setFormData((p) => ({
                                        ...p,
                                        luogo_nascita: c.nome,
                                        provincia_nascita: c.sigla_provincia,
                                    }))} />
                            </div>
                            <div className="form-group">
                                <label>Stato di nascita (se estero)</label>
                                <RicercaStato
                                    valore={formData.stato_nascita}
                                    codiceFiscale={(formData.tipo_documento || 'cf_italiano') === 'cf_italiano' ? formData.codice_fiscale : ''}
                                    onChange={(v) => setFormData((p) => ({ ...p, stato_nascita: v }))} />
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Contatti</h3>
                        <div className="form-grid">
                            <div className="form-group">
                                <label>Telefono</label>
                                <input type="tel" name="telefono" value={formData.telefono} onChange={handleChange} />
                            </div>
                            <div className="form-group">
                                <label>Email</label>
                                <input type="email" name="email" value={formData.email} onChange={handleChange} />
                                {/* L'email della scheda rimasta com'era e senza forma: non
                                    blocca il salvataggio, si segnala (Alex, 6/10/2026). */}
                                {avvisoEmail(formData.email, paziente?.email) && (
                                    <span className="campo-avviso">{avvisoEmail(formData.email, paziente?.email)}</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="form-section">
                        <h3>Residenza</h3>
                        <div className="form-grid">
                            <div className="form-group form-group-full">
                                <label>Indirizzo</label>
                                <input type="text" name="indirizzo" value={formData.indirizzo} onChange={handleChange} />
                            </div>
                            <div className="form-group">
                                <label>Città</label>
                                <RicercaComune
                                    valore={formData.citta}
                                    onChange={(v) => setFormData((p) => ({ ...p, citta: v }))}
                                    onScelta={(c) => setFormData((p) => ({
                                        ...p,
                                        citta: c.nome,
                                        provincia: c.sigla_provincia,
                                        cap: p.cap || c.cap || '',
                                    }))} />
                            </div>
                            <div className="form-group">
                                <label>CAP</label>
                                <input type="text" name="cap" value={formData.cap} onChange={handleChange} maxLength={12} />
                                <AvvisoCap cap={formData.cap} comune={formData.citta}
                                           attivo={residenzaInItalia(formData.stato_residenza)} />
                            </div>
                            <div className="form-group">
                                <label>Provincia</label>
                                <SelezioneRicerca
                                    valore={formData.provincia}
                                    onChange={(v) => setFormData(prev => ({ ...prev, provincia: v }))}
                                    opzioni={PROVINCE_ITALIANE}
                                    placeholder="Sigla…"
                                />
                            </div>
                            {/* Vuoto = Italia, come nel modulo d'accettazione:
                                per un paziente italiano non cambia niente. */}
                            <div className="form-group">
                                <label>Stato di residenza (se estero)</label>
                                <input type="text" name="stato_residenza" placeholder="Es. San Marino"
                                       value={formData.stato_residenza} onChange={handleChange} />
                            </div>
                        </div>
                    </div>

                    {/* Percorso Longevity: e' un attributo del percorso di cura,
                        non un permesso. La cartella clinica resta comunque
                        visibile a tutti i medici. */}
                    <div className="form-section">
                        <h3>Percorso</h3>
                        <div className="form-grid">
                            <div className="form-group form-group-full">
                                <label className="checkbox-inline">
                                    <input
                                        type="checkbox"
                                        name="longevity"
                                        checked={formData.longevity}
                                        onChange={handleChange}
                                    />
                                    <span>Paziente del percorso Longevity</span>
                                </label>
                                <small className="form-hint">
                                    Il paziente verrà contrassegnato con l'orologio verde in elenco
                                    e in scheda. La data di ingresso nel percorso viene registrata
                                    alla prima attivazione.
                                </small>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Note</h3>
                        <div className="form-grid">
                            <div className="form-group form-group-full">
                                <label>Note libere (visibili a tutto lo staff)</label>
                                <textarea
                                    name="note"
                                    value={formData.note}
                                    onChange={handleChange}
                                    rows={3}
                                    placeholder="Annotazioni sul paziente…"
                                    className="campo-note-edit"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" onClick={chiudi} className="btn-secondary" disabled={loading}>
                            Annulla
                        </button>
                        <button type="submit" className="btn-primary" disabled={loading}>
                            {loading ? 'Salvataggio...' : (paziente ? 'Aggiorna' : 'Crea Paziente')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PazienteForm;
